import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';

import { REGISTERS, REGISTER_ORDER } from '@/features/posts/registers';

import { FEED_SORTS, type FeedSort } from './filters';
import type { FeedFiltersApi } from './useFeedFilters';

/**
 * The two controls people reach for constantly — how the feed is sorted and
 * which registers are in it — sit directly above it rather than in the sidebar
 * panel with the ones they set once.
 *
 * The sort is a segmented control because the options are few, mutually
 * exclusive and worth seeing without opening anything; the registers are
 * toggles because they combine.
 */
export function FeedToolbar({ api }: { api: FeedFiltersApi }) {
  const { t } = useTranslation();
  const { filters, setFilters, toggleType } = api;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      component="section"
      aria-label={t('feed.sortAndRegister')}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filters.sort}
        onChange={(_event, next: FeedSort | null) => {
          // A segmented control has no "nothing selected" state: pressing the
          // active option again should leave it selected, not clear the sort.
          if (next) setFilters({ sort: next });
        }}
        aria-label={t('feed.sort')}
      >
        {FEED_SORTS.map((sort) => (
          <ToggleButton key={sort} value={sort}>
            {t(`feed.sorts.${sort}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ flexGrow: 1 }} />

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {REGISTER_ORDER.map((type) => {
          const register = REGISTERS[type];
          const selected = filters.types.includes(type);

          return (
            <Chip
              key={type}
              size="small"
              clickable
              color={selected ? register.accent : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              icon={<register.Icon fontSize="small" aria-hidden />}
              label={t(`registers.${type}.label`)}
              onClick={() => toggleType(type)}
              aria-pressed={selected}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
